import { Injector, Type, runInInjectionContext } from '@angular/core';
import { Editors, OperatorType } from 'angular-slickgrid';
import { map, of } from 'rxjs';
import {
  AdditionalGridColumnDefinition,
  ColumnConfig,
  FieldDefinition,
  ForeignKeyConfig,
  FilterFactory,
} from '../field-definition';
import { GenericValidator } from '../../generic-form/model/generic-validator';
import { GenericFormField } from '../../generic-form/model/generic-field';
import { ValidationTypes } from '../../generic-form/enums/validation-types';
import { ControlTypes } from '../../generic-form/enums/control-types';
import { createStandardFkConfig, ForeignKeyListLoader } from '../fk/fk-loader.helper';
import { createFkAsyncDisplayHandlers } from '../fk/fk-async-display.helper';
import { CustomNumberRangeFilter } from '../../generic-table/filters/custom-number-range-filter';
import { CustomDateRangeFilter } from '../../generic-table/filters/custom-date-range-filter';
import { ZnSelectFilter } from '../../generic-table/filters/zn-select-filter';
import { ZnSelectEditor } from '../../generic-table/editors/zn-select-editor';
import { BaseFieldBuilder } from './field-builder.base';
import { ZnSelectQueryParams } from '../../generic-form/interfaces/IGenericFieldOptions';

type FkOption = { key: string; value: string };

type FkBaseQuery = {
  maxResultCount: number;
  skipCount: number;
  [key: string]: unknown;
};

type FkLoaderParams = ZnSelectQueryParams & {
  ids?: Array<string | number>;
};

export class ForeignKeyFieldBuilder<TService = unknown> extends BaseFieldBuilder {
  private baseQuery: FkBaseQuery = { maxResultCount: 1000, skipCount: 0 };
  private displayField = 'name';
  private displayFieldWasSet = false;
  private keyFieldValue = 'id';
  private _required = false;
  private validators: GenericValidator[] = [];
  private fkConfig!: ForeignKeyConfig;
  private displayNestedPath: string = '';
  private sortByPath: string = '';
  private summaryByPath: string = '';
  private filterByPath: string = '';
  private customFilterType?: string | FilterFactory;
  private fixedQuery: Record<string, unknown> = {};
  private listLoader?: ForeignKeyListLoader<TService>;

  constructor(key: string, label: string, private readonly service: Type<TService>) {
    super(key, label, ControlTypes.Dropdown);
    this._type = 'foreignKey';
  }

  required(): this {
    this._required = true;
    this.validators.push(new GenericValidator({ key: ValidationTypes.Required }));
    return this;
  }

  maxResults(count: number): this {
    this.baseQuery.maxResultCount = count;
    return this;
  }

  display(field: string): this {
    this.displayField = field;
    this.displayFieldWasSet = true;
    return this;
  }

  keyField(field: string): this {
    this.keyFieldValue = field;
    return this;
  }

  displayFromNested(path: string): this {
    this.displayNestedPath = path;
    return this;
  }

  sortBy(path: string): this {
    this.sortByPath = path;
    return this;
  }

  summaryBy(path: string): this {
    this.summaryByPath = path;
    return this;
  }

  filterBy(path: string): this {
    this.filterByPath = path;
    return this;
  }

  customFilter(filterType: 'number' | 'date' | FilterFactory): this {
    this.customFilterType = filterType;
    return this;
  }

  queryFilter(query: Record<string, unknown>): this {
    this.fixedQuery = { 
      ...this.fixedQuery, 
      ...query };
    return this;
  }

  loadWith(loader: ForeignKeyListLoader<TService>): this {
    this.listLoader = loader;
    return this;
  }

  build(): FieldDefinition {
    const column = this.getBaseColumnConfig();
    const localizedLabel = this.getLocalizationKey();

    this.applyNestedDefaults();
    this.fkConfig = this.createFkConfig();

    this.configureColumnDisplay(column);

    const optionsLoader = this.createOptionsLoader();
    this.configureEditor(column, optionsLoader);
    this.configureFilter(column, optionsLoader);

    return {
      key: this.key,
      label: localizedLabel,
      column,
      additionalGridColumns: [this.createIdGridColumn()],
      fieldOptions: new GenericFormField({
        key: this.key,
        label: localizedLabel,
        controlType: this._controlType,
        type: this._type,
        required: this._required,
        validators: this.validators,
        hidden: this.hideInChildFormsValue,
        hiddenByDefault: this.hideByDefaultValue,
        readOnly: this.readOnlyValue,
        showReadOnlyInForm: this.showReadOnlyInFormValue,
        dependencies: this.dependencyRules.length ? this.dependencyRules : undefined,
        prefilter: this.prefilterOptions,
        optionsLoader,
      }),
      foreignKey: this.fkConfig,
    };
  }

  private createIdGridColumn(): AdditionalGridColumnDefinition {
    return {
      key: `${this.key}FkId`,
      labelSuffixKey: 'General::Id',
      hiddenByDefault: true,
      column: {
        field: this.key,
        sortable: false,
        filterable: false,
        editable: false,
        focusable: false,
        formatter: (_row: number, _cell: number, value: unknown) =>
          this.toNestedDisplayText(value) ?? '',
        params: {
          excludeFromExport: true,
        },
      },
    };
  }

  private applyNestedDefaults(): void {
    if (!this.displayNestedPath) {
      return;
    }

    if (!this.displayFieldWasSet && this.displayField === 'name') {
      const parts = this.displayNestedPath.split('.');
      const last = parts[parts.length - 1];
      if (last) {
        this.displayField = last;
      }
    }

    if (!this.sortByPath) {
      this.sortByPath = this.displayNestedPath;
    }

    if (!this.summaryByPath) {
      this.summaryByPath = this.displayNestedPath;
    }
  }

  private createFkConfig(): ForeignKeyConfig {
    return createStandardFkConfig(
      this.service,
      this.baseQuery,
      this.displayField,
      this.keyFieldValue,
      this.listLoader
    );
  }

  private configureColumnDisplay(column: ColumnConfig): void {
    if (this.displayNestedPath) {
      column.params = {
        ...(column.params ?? {}),
        displayNestedPath: this.displayNestedPath,
      };

      if (this.sortByPath) {
        column.params = {
          ...(column.params ?? {}),
          queryFieldSorter: this.sortByPath,
        };
      }

      if (this.summaryByPath) {
        column.params = {
          ...(column.params ?? {}),
          summaryField: this.summaryByPath,
        };
      }

      if (this.filterByPath) {
        column.params = {
          ...(column.params ?? {}),
          queryFieldFilter: this.filterByPath,
        };
      }

      column.formatter = (
        _row: number,
        _cell: number,
        _value: unknown,
        _colDef: unknown,
        dataContext: unknown
      ) => {
        const displayValue = this.toNestedDisplayText(
          this.resolvePath(dataContext, this.displayNestedPath)
        );
        if (displayValue === undefined) {
          return '';
        }
        return displayValue;
      };

      return;
    }

    const { formatter, createAsyncPostRender } = createFkAsyncDisplayHandlers(
      this.fkConfig,
      (item, displayField) => this.resolveDisplayValue(item, displayField)
    );

    column.formatter = formatter;
    column.createAsyncPostRender = createAsyncPostRender;
  }

  private createOptionsLoader(): (params?: FkLoaderParams) => ReturnType<ForeignKeyConfig['loader']> {
    return (params?: FkLoaderParams) => {
      const displayField = this.getDisplayField();
      const keyField = this.getKeyField();
      const isNested = !!this.displayNestedPath;
      const effectiveParams = this.normalizeParams(params, isNested);

      if (isNested && params === undefined) {
        return of([] as FkOption[]);
      }

      if (
        effectiveParams.ids?.length &&
        !effectiveParams.search &&
        !effectiveParams.preferCache &&
        !this.listLoader
      ) {
        if (!this.fkConfig.getByIds) {
          throw new Error('FK service does not expose getListByIds(input)');
        }

        return this.fkConfig.getByIds(effectiveParams.ids, {
          skipHandleError: true,
          forceRefresh: effectiveParams.forceRefresh,
        }).pipe(
          map((items: unknown[]) => items
            .map(item => this.toOption(item, displayField, keyField))
            .filter((option): option is FkOption => option !== undefined)),
        );
      }

      const queryOverride = this.buildQueryOverride(effectiveParams, displayField, keyField);

      return this.fkConfig.loader(
        effectiveParams.forceRefresh,
        queryOverride,
        { skipHandleError: true }
      ).pipe(
        map((items: unknown[]) => items
          .map(item => this.toOption(item, displayField, keyField))
          .filter((option): option is FkOption => option !== undefined)),
      );
    };
  }

  private normalizeParams(params: FkLoaderParams | undefined, isNested: boolean): FkLoaderParams {
    const idsProvided = !!params?.ids?.length;
    const defaultMax = isNested ? 20 : this.baseQuery.maxResultCount;
    const maxResultCount = idsProvided ? this.baseQuery.maxResultCount : defaultMax;

    return {
      skipCount: 0,
      maxResultCount,
      ...params,
    };
  }

  private buildQueryOverride(
    params: FkLoaderParams,
    displayField: string,
    keyField: string
  ): Record<string, unknown> {
    const queryOverride: Record<string, unknown> = params.filterContext
      ? { ...params.filterContext, ...this.fixedQuery }
      : { ...this.fixedQuery };

    if (params.search) {
      queryOverride[displayField] = params.search;
    }
    if (!queryOverride.sorting) {
      queryOverride.sorting = displayField;
    }
    if (params.ids?.length && !params.preferCache) {
      queryOverride[keyField] = params.ids;
    }
    if (params.skipCount !== undefined) {
      queryOverride.skipCount = params.skipCount;
    }
    if (params.maxResultCount !== undefined) {
      queryOverride.maxResultCount = params.maxResultCount;
    }

    return queryOverride;
  }

  private toOption(item: unknown, displayField: string, keyField: string): FkOption | undefined {
    const key = this.getItemValue(item, keyField);
    if (!((typeof key === 'string' && key.trim().length > 0) ||
      (typeof key === 'number' && Number.isFinite(key)))) {
      return undefined;
    }
    return {
      key: String(key),
      value: String(this.resolveDisplayValue(item, displayField)),
    };
  }

  private resolveDisplayValue(item: unknown, displayField: string): unknown {
    if (displayField.includes('.')) {
      return this.resolvePath(item, displayField);
    }
    return this.getItemValue(item, displayField);
  }

  private toNestedDisplayText(value: unknown): string | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    if (value instanceof Date) {
      return value.toString();
    }

    switch (typeof value) {
      case 'string':
        return value;
      case 'number':
      case 'boolean':
      case 'bigint':
      case 'symbol':
        return value.toString();
      default:
        return undefined;
    }
  }

  private resolvePath(obj: unknown, path: string): unknown {
    if (!obj || typeof obj !== 'object') {
      return undefined;
    }
    return path
      .split('.')
      .reduce<unknown>((cur, part) => {
        if (!cur || typeof cur !== 'object') {
          return undefined;
        }
        return (cur as Record<string, unknown>)[part];
      }, obj as Record<string, unknown>);
  }

  private getItemValue(item: unknown, key: string): unknown {
    if (!item || typeof item !== 'object') {
      return undefined;
    }
    return (item as Record<string, unknown>)[key];
  }

  private getDisplayField(): string {
    return this.fkConfig.displayField || 'name';
  }

  private getKeyField(): string {
    return this.fkConfig.keyField || 'id';
  }

  private configureEditor(
    column: ColumnConfig,
    optionsLoader: (params?: FkLoaderParams) => ReturnType<ForeignKeyConfig['loader']>
  ): void {
    if (this.readOnlyValue) {
      return;
    }

    column.createEditor = (_localizationService: unknown, injector: Injector) => {
      return {
        model: ZnSelectEditor,
        optionsLoader: (params?: FkLoaderParams) =>
          runInInjectionContext(injector, () => optionsLoader(params)),
        errorEntityKey: this.label,
        required: this._required,
        injector,
      };
    };
  }

  private configureFilter(
    column: ColumnConfig,
    optionsLoader: (params?: FkLoaderParams) => ReturnType<ForeignKeyConfig['loader']>
  ): void {
    if (this.customFilterType) {
      if (typeof this.customFilterType === 'string') {
        switch (this.customFilterType) {
          case 'number':
            column.createFilter = (localizationService: unknown) => ({
              model: CustomNumberRangeFilter,
              params: { localizationService },
            });
            break;
          case 'date':
            column.createFilter = (localizationService: unknown) => ({
              model: CustomDateRangeFilter,
              params: { localizationService, includeTime: false },
            });
            break;
          default:
            throw new Error(`Unknown filter type: ${this.customFilterType}`);
        }
      } else {
        column.createFilter = this.customFilterType;
      }
      return;
    }

    column.createFilter = (_localizationService: unknown, injector: Injector) => ({
      model: ZnSelectFilter,
      operator: OperatorType.in,
      params: {
        injector,
        optionsLoader: (params?: FkLoaderParams) =>
          runInInjectionContext(injector, () => optionsLoader(params)),
        errorEntityKey: this.label,
      },
    });
  }
}
